import { Action, createReducer, on } from '@ngrx/store';

import { JobAlertDto } from 'src/app/shared/models/job-alert.model';
import * as fromActions from '../actions/';
import { RecruitingDashboardState } from '../state/recruiting-dashboard.state';
import { JobAlertRuleDisplay } from '../../../recruiting/dashboard/models/job-alert-rule-display.model';

const initialState: RecruitingDashboardState = {
    isHubConnectionActive: false,
    jobAlerts: [],
    newJobAlerts: {},
    jobAlertRules: [],
    jobAlertsLastViewedAt: null,
    leadAssignedTo: null,
    recruitingDashboardAlertData: null,
    recruitingDashboardJobAlertData: null,
    error: undefined
};

export function recruitingDashboardReducer(state: RecruitingDashboardState, action: Action) {
    return createReducer(
        initialState,
        on(fromActions.RecruitingActions.Dashboard.jobAlertsSuccess, (state, action) => ({
            ...state,
            jobAlerts: [...action.jobAlerts],
            newJobAlerts: action.jobAlerts.reduce(
                (prev: Record<number, JobAlertDto>, current: JobAlertDto) => {
                    prev[current.jobId] = current;
                    return prev;
                },
                {} as Record<number, JobAlertDto>
            )
        })),

        on(fromActions.RecruitingActions.Dashboard.jobUpdatedAlertsSuccess, (state, action) => ({
            ...state,
            jobAlerts: [...state.jobAlerts, action.jobAlert],
            newJobAlerts: { ...state.newJobAlerts, [action.jobAlert.jobId]: action.jobAlert }
        })),

        on(
            fromActions.RecruitingActions.Dashboard.jobAlertsFailure,
            fromActions.RecruitingActions.Dashboard.jobUpdatedAlertsFailure,
            fromActions.RecruitingActions.Dashboard.leadAssignedAlertFailure,
            fromActions.RecruitingActions.Dashboard.recruitingDashboardAlertFailure,
            fromActions.RecruitingActions.Dashboard.recruitingDashboardJobAlertFailure,
            fromActions.RecruitingActions.Dashboard.getJobAlertRulesFailure,
            fromActions.RecruitingActions.Dashboard.getJobAlertRuleFailure,
            fromActions.RecruitingActions.Dashboard.getLatestJobAlertsViewFailure,
            fromActions.RecruitingActions.Dashboard.addJobAlertsViewEventFailure,
            fromActions.RecruitingActions.Dashboard.updateJobAlertRuleFailure,
            (state, action) => ({
                ...state,
                error: action.error
            })
        ),

        on(fromActions.RecruitingActions.Dashboard.leadAssignedAlertSuccess, (state, action) => ({
            ...state,
            leadAssignedTo: action.message
        })),

        on(fromActions.RecruitingActions.Dashboard.recruitingDashboardAlertSuccess, (state, action) => ({
            ...state,
            recruitingDashboardAlertData: action.data
        })),

        on(fromActions.RecruitingActions.Dashboard.recruitingDashboardJobAlertSuccess, (state, action) => ({
            ...state,
            recruitingDashboardJobAlertData: action.data
        })),

        on(fromActions.RecruitingActions.Dashboard.getJobAlertRulesSuccess, (state, action) => ({
            ...state,
            jobAlertRules: action.jobAlertRules
        })),

        on(fromActions.RecruitingActions.Dashboard.getJobAlertRuleSuccess, (state, action) => ({
            ...state,
            jobAlertRules: addOrUpdateJobAlertRule(state.jobAlertRules, action.jobAlertRule)
        })),

        on(fromActions.RecruitingActions.Dashboard.deleteJobAlertRuleSuccess, (state, action) => ({
            ...state,
            jobAlertRules: removeJobAlertRule(state.jobAlertRules, action.ruleId)
        })),

        on(fromActions.RecruitingActions.Dashboard.getLatestJobAlertsViewSuccess, (state, action) => ({
            ...state,
            jobAlertsLastViewedAt: action.eventTimestamp.toJSON()
        }))
    )(state, action);
}

export function reducer(state: RecruitingDashboardState | undefined, action: Action) {
    return recruitingDashboardReducer(state, action);
}

function removeJobAlertRule(rules: JobAlertRuleDisplay[], ruleIdToDelete: number) {
    const ruleIndex = rules.findIndex((rule: JobAlertRuleDisplay) => rule.jobAlertRuleId === ruleIdToDelete);
    if (ruleIndex === -1) {
        return rules;
    }
    return [...rules.slice(0, ruleIndex), ...rules.slice(ruleIndex + 1)];
}

function addOrUpdateJobAlertRule(rules: JobAlertRuleDisplay[], ruleToAddOrUpdate: JobAlertRuleDisplay) {
    const ruleIndex = rules.findIndex((rule: JobAlertRuleDisplay) => rule.jobAlertRuleId === ruleToAddOrUpdate.jobAlertRuleId);
    if (ruleIndex === -1) {
        return [...rules, ruleToAddOrUpdate];
    }
    return rules.map(rule =>
        rule.jobAlertRuleId === ruleToAddOrUpdate.jobAlertRuleId ? ruleToAddOrUpdate : rule
    );
}
